import { toISODate } from "./utils";

export type HolidayInput = {
  date: string;
  name: string;
  type: "regular" | "special";
  worked: boolean;
};

export type CutoffMode = "first_half" | "second_half" | "full_period";

export type GovColumn = {
  key: "hdmf" | "philhealth" | "sss" | "withholdingTax";
  label: string;
};

export const GOV_COLUMNS: GovColumn[] = [
  { key: "hdmf", label: "HDMF" },
  { key: "philhealth", label: "PhilHealth" },
  { key: "sss", label: "SSS" },
  { key: "withholdingTax", label: "Withholding Tax" },
];

export function resolveCutoffMode(startDate: string, endDate: string): CutoffMode {
  const startDay = Number(startDate?.slice(-2) || 0);
  const endDay = Number(endDate?.slice(-2) || 0);
  
  // First Half (e.g. 24th to 8th, or 1st to 15th)
  if (endDay > 0 && endDay <= 15) return "first_half";
  
  // Second Half (e.g. 9th to 23rd, or 16th to 30th)
  // If it starts after the 1st, it's a semi-monthly second half.
  if (startDay > 1) return "second_half";
  
  return "full_period";
}

export function activeGovColumnsForCutoff(cutoffMode: CutoffMode) {
  if (cutoffMode === "first_half") return GOV_COLUMNS.filter((col) => col.key === "hdmf" || col.key === "philhealth");
  if (cutoffMode === "second_half") return GOV_COLUMNS.filter((col) => col.key === "sss" || col.key === "withholdingTax");
  return GOV_COLUMNS;
}
export function holidayMultiplier(type: "regular" | "special", worked: boolean): number {
  if (type === "regular") return worked ? 2.0 : 1.0; // PH: unworked regular holiday is still paid 100%
  return worked ? 1.3 : 0.0; // special non-working: no work no pay, 130% if worked
}

export function holidayTypeLabel(type: "regular" | "special", worked: boolean): string {
  if (type === "regular") return worked ? "Regular \u00b7 Worked \u00b7 200%" : "Regular \u00b7 Unworked \u00b7 100%";
  return worked ? "Special \u00b7 Worked \u00b7 130%" : "Special \u00b7 Unworked \u00b7 0%";
}

export function listWeekdays(start: string, end: string): string[] {
  const out: string[] = [];
  const d = new Date(start + "T00:00:00");
  const last = new Date(end + "T00:00:00");
  if (isNaN(d.getTime()) || isNaN(last.getTime())) return out;
  let guard = 0;
  while (d <= last && guard < 400) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(toISODate(d));
    d.setDate(d.getDate() + 1);
    guard++;
  }
  return out;
}

/* ------------------------------ quick range presets ---------------------------- */
export type PresetShape = {
  label: string;
  mode: "monthly" | "fixed";
  startDay: number;
  startMonthOffset?: number; // Offset relative to the base monthOffset
  endDay: number; // 0 = last day of the month
  monthOffset: number; // 0 = this month, -1 = previous, 1 = next
  startDate: string | null;
  endDate: string | null;
};

export const DEFAULT_PRESETS: PresetShape[] = [
  { label: "First Half", mode: "monthly", startDay: 24, startMonthOffset: -1, endDay: 8, monthOffset: 0, startDate: null, endDate: null },
  { label: "Second Half", mode: "monthly", startDay: 9, endDay: 23, monthOffset: 0, startDate: null, endDate: null },
  { label: "Full month", mode: "monthly", startDay: 1, endDay: 0, monthOffset: 0, startDate: null, endDate: null },
];

export const MONTH_OFFSET_LABELS: Record<number, string> = {
  [-2]: "2 months ago",
  [-1]: "Previous month",
  0: "This month",
  1: "Next month",
};

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/** Turns a stored preset into a concrete date range for the given reference date. */
export function resolvePreset(
  preset: PresetShape,
  ref: Date = new Date()
): { start: string; end: string } | null {
  if (preset.mode === "fixed") {
    if (!preset.startDate || !preset.endDate) return null;
    if (preset.startDate > preset.endDate) return null;
    return { start: preset.startDate, end: preset.endDate };
  }

  const baseMonth = ref.getMonth() + (preset.monthOffset || 0);

  // Start Date calculation
  const startBase = new Date(ref.getFullYear(), baseMonth + (preset.startMonthOffset || 0), 1);
  const startY = startBase.getFullYear();
  const startM = startBase.getMonth() + 1;
  const startEom = new Date(startY, startM, 0).getDate();
  const startDay = Math.min(Math.max(1, preset.startDay || 1), startEom);

  // End Date calculation
  const endBase = new Date(ref.getFullYear(), baseMonth, 1);
  const endY = endBase.getFullYear();
  const endM = endBase.getMonth() + 1;
  const endEom = new Date(endY, endM, 0).getDate();
  const rawEnd = preset.endDay === 0 ? endEom : Math.min(Math.max(1, preset.endDay), endEom);

  const start = `${startY}-${pad(startM)}-${pad(startDay)}`;
  let end = `${endY}-${pad(endM)}-${pad(rawEnd)}`;

  // Basic validation: end shouldn't be before start
  if (start > end) {
    end = `${startY}-${pad(startM)}-${pad(startEom)}`;
  }

  return { start, end };
}

/** Short human description of a preset, e.g. "Day 16 -> end of month - This month". */
export function describePreset(preset: PresetShape): string {
  if (preset.mode === "fixed") {
    if (!preset.startDate || !preset.endDate) return "Set both dates";
    return `${preset.startDate} \u2192 ${preset.endDate}`;
  }
  const start = preset.startMonthOffset === -1 ? `day ${preset.startDay} (prev month)` : `day ${preset.startDay}`;
  const end = preset.endDay === 0 ? "end of month" : `day ${preset.endDay}`;
  return `${start} \u2192 ${end} \u00b7 ${MONTH_OFFSET_LABELS[preset.monthOffset] ?? "This month"}`;
}

export function suggestPeriodName(start: string, end: string): string {
  if (!start || !end) return "";
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return "";
  
  const mode = resolveCutoffMode(start, end);
  const m = (d: Date) => d.toLocaleDateString("en-PH", { month: "short" });
  const full = (d: Date) => d.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });
  
  let prefix = "";
  if (mode === "first_half") prefix = "First Half - ";
  else if (mode === "second_half") prefix = "Second Half - ";
  
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const name = sameMonth
    ? `${m(s)} ${s.getDate()} \u2013 ${e.getDate()}, ${s.getFullYear()}`
    : `${full(s)} \u2013 ${full(e)}`;
    
  return `${prefix}${name}`;
}
